/**
 * Standalone Face Upload Service
 * Uses fetch API to avoid axios configuration issues
 * Completely independent from existing API setup
 */

const getAPIBase = () => {
  // Use environment variable or default to localhost
  const envUrl = process.env.REACT_APP_API_URL;
  
  if (envUrl) {
    // Ensure it ends with /api if not already
    if (!envUrl.includes('/api')) {
      return `${envUrl}/api`;
    }
    return envUrl;
  }
  
  // Default to local development
  return 'http://localhost:5000/api';
};

const API_BASE = getAPIBase();

class FaceUploadService {
  /**
   * Generic upload method using fetch API
   * @param {string} endpoint - API endpoint
   * @param {File} file - File to upload
   * @param {string} fieldName - Form field name
   * @returns {Promise} Response data
   */
  static async upload(endpoint, file, fieldName) {
    console.log(`📤 FaceUploadService: Uploading ${fieldName} to ${endpoint}`);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required. Please login.');
    }
    
    // Create FormData
    const formData = new FormData();
    formData.append(fieldName, file);
    
    // Log for debugging
    console.log('📎 File details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      fieldName: fieldName
    });
    
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
          // DO NOT set Content-Type - browser will set it with boundary
        }
      });
      
      // Parse response
      const data = await response.json();
      
      if (!response.ok) {
        console.error(`❌ Upload failed (${response.status}):`, data);
        throw new Error(data.message || `Upload failed with status ${response.status}`);
      }
      
      console.log('✅ Upload successful:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Network/upload error:', error.message);
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection.');
      }
      
      throw error;
    }
  }
  
  /**
   * Upload profile face image
   * @param {File} file - Profile image file
   * @returns {Promise} Upload response
   */
  static async uploadProfileImage(file) {
    return this.upload('/face-verification/profile-image', file, 'faceImage');
  }
  
  /**
   * Verify face with selfie
   * @param {File} selfieFile - Selfie image file
   * @returns {Promise} Verification response
   */
  static async verifyFace(selfieFile) {
    return this.upload('/face-verification/verify', selfieFile, 'selfie');
  }
  
  /**
   * Check face verification status
   * @returns {Promise} Status response
   */
  static async checkStatus() {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/face-verification/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to check status');
    }
    
    return data;
  }
  
  /**
   * Reset face verification
   * @returns {Promise} Reset response
   */
  static async resetVerification() {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/face-verification/reset`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset verification');
    }
    
    return data;
  }
}

export default FaceUploadService;